import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Paperclip, Upload, Trash2, Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fmtSize = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} КБ`;
  return `${(n / 1024 / 1024).toFixed(2)} МБ`;
};

/**
 * Универсальный блок «Вложения к заявке».
 *
 * Props:
 *   applicationId — id заявки (если null/undefined — блок не показывается)
 *   canEdit       — true, если текущий пользователь может загружать/удалять
 *                   (поставщик-владелец на стадии pending/rejected; организатор всегда)
 *   compact       — если true, шрифт мельче (используется в админ-панели)
 */
export default function ApplicationAttachments({ applicationId, canEdit = false, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const reload = async () => {
    if (!applicationId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/applications/${applicationId}/attachments`);
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [applicationId]);

  if (!applicationId) return null;

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let okCount = 0;
    for (const f of files) {
      try {
        const form = new FormData();
        form.append('file', f);
        await api.post(`/applications/${applicationId}/attachments`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        okCount += 1;
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || 'ошибка загрузки'}`);
      }
    }
    setUploading(false);
    if (okCount) toast.success(`Загружено файлов: ${okCount}`);
    if (inputRef.current) inputRef.current.value = '';
    await reload();
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Удалить файл «${name}»?`)) return;
    try {
      await api.delete(`/applications/${applicationId}/attachments/${id}`);
      toast.success('Файл удалён');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Не удалось удалить');
    }
  };

  const handleDownload = async (id, name) => {
    try {
      const r = await api.get(`/applications/${applicationId}/attachments/${id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = name || 'file';
      document.body.appendChild(a); a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Не удалось скачать');
    }
  };

  const t = compact ? 'text-[11px]' : 'text-xs';
  return (
    <div className={`space-y-1.5 ${compact ? 'pt-1' : 'pt-2 mt-2 border-t border-slate-100'}`} data-testid="application-attachments">
      <div className="flex items-center justify-between">
        <span className={`${t} text-slate-600 inline-flex items-center gap-1.5`}>
          <Paperclip className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
          Вложения {items.length > 0 && <span className="text-slate-400">({items.length})</span>}
        </span>
        {canEdit && (
          <>
            <Button size="sm" variant="ghost" onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="att-upload-btn"
              className="h-6 px-2 text-[11px] rounded-sm">
              {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
              Добавить
            </Button>
            <input ref={inputRef} type="file" multiple
              onChange={(e) => handleUpload(Array.from(e.target.files || []))}
              className="hidden"
              data-testid="att-file-input"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip,.txt,.csv,.rar,.7z" />
          </>
        )}
      </div>
      {loading ? (
        <div className={`${t} text-slate-400`}>Загрузка…</div>
      ) : items.length === 0 ? (
        <div className={`${t} text-slate-400 italic`}>
          {canEdit ? 'Файлов нет. Можно приложить документы (pdf, doc, xls, изображения и т.п.)' : 'Файлов нет'}
        </div>
      ) : (
        <div className="space-y-0.5">
          {items.map(it => (
            <div key={it.id}
              className="flex items-center gap-2 py-0.5 px-1 rounded-sm hover:bg-slate-50"
              data-testid={`att-row-${it.id}`}>
              <FileText className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-slate-400 shrink-0`} />
              <button type="button"
                onClick={() => handleDownload(it.id, it.filename)}
                className={`${t} text-blue-700 hover:underline truncate flex-1 text-left`}
                title={it.filename}
                data-testid={`att-download-${it.id}`}>
                {it.filename}
              </button>
              <span className={`${t} text-slate-400 tabular-nums shrink-0`}>{fmtSize(it.size)}</span>
              <button type="button"
                onClick={() => handleDownload(it.id, it.filename)}
                className="text-slate-400 hover:text-blue-700"
                title="Скачать">
                <Download className="w-3 h-3" />
              </button>
              {canEdit && (
                <button type="button"
                  onClick={() => handleDelete(it.id, it.filename)}
                  className="text-slate-400 hover:text-rose-600"
                  data-testid={`att-delete-${it.id}`}
                  title="Удалить">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
